import PlateService from 'business/services/PlateService';
import { ModalContext } from 'components/modal/modalContext';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  FmlxTableBody,
  FmlxTableCell,
  FmlxTableContainer,
  FmlxTableHead,
  FmlxTableHeadCell,
  FmlxTableRow,
  FmlxTextInput,
  FmlxTextBox,
  FmlxTooltip,
} from 'fmlx-common-ui';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { showAlertSnackBar } from 'utils/Global';
import './EditBarcodeBulkModal.scss';
import { getErrorMessage, getListDuplicatePlateIds } from './editBarcodeUtils';
import {
  BARCODE_MAX_LENGTH,
  BarcodeFieldError,
  EditBarcodeBulkModalHandlersRef,
  EditBarcodeRow,
  EditBarcodeRowValidation,
  SYSTEM_CHECK_DEBOUNCE_MS,
  TABLE_COLUMNS,
  TABLE_ID,
} from './constants';

const plateService = new PlateService();

interface Props {
  rows: EditBarcodeRow[];
  modalHandlersRef: React.MutableRefObject<EditBarcodeBulkModalHandlersRef>;
}

const EditBarcodeBulkModalBody = (props: Props) => {
  const { rows: initialRows, modalHandlersRef } = props;
  const { setModalDetails } = useContext(ModalContext);
  const [rows, setRows] = useState<EditBarcodeRow[]>(initialRows);
  const [systemDuplicatePlateIds, setSystemDuplicatePlateIds] = useState<Set<number>>(
    new Set(),
  );
  const [checkingSystemPlateIds, setCheckingSystemPlateIds] = useState<Set<number>>(
    new Set(),
  );
  const [defaultBarcodes, setDefaultBarcodes] = useState<Record<number, string>>({});
  const systemCheckRequestIds = useRef<Record<number, number>>({});
  const systemCheckRequestCounter = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadDefaultBarcodes = async () => {
      const entries = await Promise.all(
        initialRows.map(async (row) => {
          try {
            const result = await plateService.GetPlateDefaultBarcode(row.plateId);
            return [row.plateId, result?.barcode ?? ''] as const;
          } catch {
            return [row.plateId, ''] as const;
          }
        }),
      );

      if (!cancelled) {
        setDefaultBarcodes(Object.fromEntries(entries));
      }
    };

    loadDefaultBarcodes();

    return () => {
      cancelled = true;
    };
  }, [initialRows]);

  const listDuplicatePlateIds = useMemo(() => getListDuplicatePlateIds(rows), [rows]);

  /**
   * Debounces a server-side barcode availability check for a plate row.
   * Skips the API call when the barcode is empty or unchanged from its initial value.
   * Uses request IDs so only the latest check for a plate updates validation state.
   */
  const queueSystemBarcodeCheck = useCallback(
    (plateId: number, barcode: string) => {
      const trimmedBarcode = barcode.trim();
      const initialRow = initialRows.find((row) => row.plateId === plateId);
      const requestId = ++systemCheckRequestCounter.current;
      systemCheckRequestIds.current[plateId] = requestId;

      if (!trimmedBarcode || trimmedBarcode === initialRow?.initialBarcode) {
        setSystemDuplicatePlateIds((prev) => {
          if (!prev.has(plateId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(plateId);
          return next;
        });
        setCheckingSystemPlateIds((prev) => {
          if (!prev.has(plateId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(plateId);
          return next;
        });
        return;
      }

      setCheckingSystemPlateIds((prev) => new Set(prev).add(plateId));

      window.setTimeout(async () => {
        if (systemCheckRequestIds.current[plateId] !== requestId) {
          return;
        }

        try {
          const isAvailable = await plateService.IsBarcodeAvailable(
            plateId,
            trimmedBarcode,
          );
          if (systemCheckRequestIds.current[plateId] !== requestId) {
            return;
          }

          setSystemDuplicatePlateIds((prev) => {
            const next = new Set(prev);
            if (isAvailable) {
              next.delete(plateId);
            } else {
              next.add(plateId);
            }
            return next;
          });
        } finally {
          if (systemCheckRequestIds.current[plateId] === requestId) {
            setCheckingSystemPlateIds((prev) => {
              const next = new Set(prev);
              next.delete(plateId);
              return next;
            });
          }
        }
      }, SYSTEM_CHECK_DEBOUNCE_MS);
    },
    [initialRows],
  );

  useEffect(() => {
    rows.forEach((row) => {
      queueSystemBarcodeCheck(row.plateId, row.barcode);
    });
  }, [rows, queueSystemBarcodeCheck]);

  const getRowValidation = (row: EditBarcodeRow): EditBarcodeRowValidation => {
    const trimmedBarcode = row.barcode.trim();
    let fieldError: BarcodeFieldError = null;

    if (!trimmedBarcode) {
      fieldError = 'empty';
    } else if (
      listDuplicatePlateIds.has(row.plateId) ||
      systemDuplicatePlateIds.has(row.plateId)
    ) {
      fieldError = 'duplicate';
    }

    return {
      fieldError,
      isCheckingSystem: checkingSystemPlateIds.has(row.plateId),
    };
  };

  const hasChanges = rows.some((row) => row.barcode !== row.initialBarcode);

  const isConfirmDisabled = useMemo(() => {
    if (!hasChanges) {
      return true;
    }

    if (checkingSystemPlateIds.size > 0) {
      return true;
    }

    return rows.some((row) => {
      const trimmedBarcode = row.barcode.trim();
      if (!trimmedBarcode) {
        return true;
      }
      if (listDuplicatePlateIds.has(row.plateId)) {
        return true;
      }
      if (systemDuplicatePlateIds.has(row.plateId)) {
        return true;
      }
      return false;
    });
  }, [
    rows,
    hasChanges,
    checkingSystemPlateIds,
    listDuplicatePlateIds,
    systemDuplicatePlateIds,
  ]);

  useEffect(() => {
    modalHandlersRef.current.getUpdates = () =>
      rows
        .filter((row) => row.barcode !== row.initialBarcode)
        .map((row) => ({ plateId: row.plateId, barcode: row.barcode.trim() }));

    setModalDetails((prev) => ({
      ...prev,
      okayDisabled: isConfirmDisabled,
    }));
  }, [rows, isConfirmDisabled, modalHandlersRef, setModalDetails]);

  const onBarcodeChange = useCallback((plateId: number, value: string) => {
    if (value.length > BARCODE_MAX_LENGTH) {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        `Barcode length should not exceed ${BARCODE_MAX_LENGTH} characters.`,
      );
      return;
    }

    setRows((prev) =>
      prev.map((row) => (row.plateId === plateId ? { ...row, barcode: value } : row)),
    );
  }, []);

  const applyRowBarcode = useCallback((plateId: number, barcode: string) => {
    systemCheckRequestIds.current[plateId] = ++systemCheckRequestCounter.current;
    setSystemDuplicatePlateIds((prev) => {
      if (!prev.has(plateId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(plateId);
      return next;
    });
    setCheckingSystemPlateIds((prev) => {
      if (!prev.has(plateId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(plateId);
      return next;
    });
    setRows((prev) =>
      prev.map((row) => (row.plateId === plateId ? { ...row, barcode } : row)),
    );
  }, []);

  const onResetRow = useCallback(
    (plateId: number) => {
      const defaultBarcode = defaultBarcodes[plateId];
      if (!defaultBarcode) {
        return;
      }

      applyRowBarcode(plateId, defaultBarcode);
    },
    [defaultBarcodes, applyRowBarcode],
  );

  const onUndoRow = useCallback(
    (plateId: number) => {
      const initialRow = initialRows.find((row) => row.plateId === plateId);
      if (!initialRow) {
        return;
      }

      applyRowBarcode(plateId, initialRow.initialBarcode);
    },
    [initialRows, applyRowBarcode],
  );

  const tableHeaders = useMemo(
    () => (
      <FmlxTableHead id={`${TABLE_ID}-fmlxtablehead`}>
        <FmlxTableRow id={`${TABLE_ID}-fmlxtablerow-header`} onClick={null}>
          {TABLE_COLUMNS.map((column) => (
            <FmlxTableHeadCell
              id={`${TABLE_ID}-fmlxtableheadcell-${column.id || 'error'}`}
              key={column.id || 'error'}
              label={column.title}
              align={column.textAlign}
              sortDisabled
              onClickSort={null}
              size="small"
              className="sticky top-0 z-10 text-nowrap"
              sx={
                column.id === 'error' ? { width: '10%', padding: '4px 8px' } : undefined
              }
            />
          ))}
        </FmlxTableRow>
      </FmlxTableHead>
    ),
    [],
  );

  const tableBody = useMemo(
    () => (
      <FmlxTableBody
        id={`${TABLE_ID}-fmlxtablebody`}
        sx={{
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {rows.map((row) => {
          const validation = getRowValidation(row);
          const errorMessage = getErrorMessage(validation.fieldError);
          const isResetDisabled = !defaultBarcodes[row.plateId]?.length;
          const isUndoDisabled = row.barcode === row.initialBarcode;

          return (
            <FmlxTableRow
              id={`${TABLE_ID}-fmlxtablerow-${row.plateId}`}
              key={row.plateId}
              onClick={null}
            >
              <FmlxTableCell
                id={`${TABLE_ID}-fmlxtablecell-error-${row.plateId}`}
                align="center"
              >
                {validation.fieldError ? (
                  <FmlxTooltip
                    id={`${TABLE_ID}-error-tooltip-${row.plateId}`}
                    title={errorMessage}
                    placement={FmlxTooltip.Placement.BOTTOM}
                  >
                    <span
                      id={`${TABLE_ID}-error-icon-wrapper-${row.plateId}`}
                      className="inline-flex items-center justify-center cursor-pointer"
                      aria-label={errorMessage}
                    >
                      <RMSvgIcon
                        id={`${TABLE_ID}-error-icon-${row.plateId}`}
                        name="Error"
                        fill="#FF5252"
                        className="w-4 h-4"
                      />
                    </span>
                  </FmlxTooltip>
                ) : null}
              </FmlxTableCell>
              <FmlxTableCell
                id={`${TABLE_ID}-fmlxtablecell-plateid-${row.plateId}`}
                align="left"
              >
                <div className="flex flex-col">
                  <span>{row.plateLabel}</span>
                  {row.lotLabel && (
                    <span className="text-[#5D5D5D] text-sm">{row.lotLabel}</span>
                  )}
                </div>
              </FmlxTableCell>
              <FmlxTableCell
                id={`${TABLE_ID}-fmlxtablecell-barcode-${row.plateId}`}
                align="left"
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <FmlxTextBox
                      id={`${TABLE_ID}-input-${row.plateId}`}
                      mode={FmlxTextInput.Mode.TEXT}
                      size={FmlxTextInput.Size.SMALL}
                      variant={FmlxTextInput.Variant.OUTLINE}
                      decoration={FmlxTextInput.Decoration.NONE}
                      maxLength={BARCODE_MAX_LENGTH}
                      value={row.barcode}
                      onChange={({ value }) => onBarcodeChange(row.plateId, value)}
                    />
                  </div>
                  <FmlxTooltip
                    id={`${TABLE_ID}-undo-tooltip-${row.plateId}`}
                    title="Undo"
                    placement={FmlxTooltip.Placement.BOTTOM}
                  >
                    <button
                      id={`${TABLE_ID}-undo-${row.plateId}`}
                      type="button"
                      disabled={isUndoDisabled}
                      onClick={() => onUndoRow(row.plateId)}
                      aria-label="Undo barcode"
                      className="edit-barcode-row-action-btn p-2 rounded-md flex items-center justify-center shrink-0"
                    >
                      <RMSvgIcon name="Undo" noFill width="1rem" height="1rem" />
                    </button>
                  </FmlxTooltip>
                  <FmlxTooltip
                    id={`${TABLE_ID}-reset-tooltip-${row.plateId}`}
                    title="Reset"
                    placement={FmlxTooltip.Placement.BOTTOM}
                  >
                    <button
                      id={`${TABLE_ID}-reset-${row.plateId}`}
                      type="button"
                      disabled={isResetDisabled}
                      onClick={() => onResetRow(row.plateId)}
                      aria-label="Reset barcode"
                      className="edit-barcode-row-action-btn p-2 rounded-md flex items-center justify-center shrink-0"
                    >
                      <RMSvgIcon name="Revert" noFill width="1rem" height="1rem" />
                    </button>
                  </FmlxTooltip>
                </div>
              </FmlxTableCell>
            </FmlxTableRow>
          );
        })}
      </FmlxTableBody>
    ),
    [
      rows,
      defaultBarcodes,
      listDuplicatePlateIds,
      systemDuplicatePlateIds,
      checkingSystemPlateIds,
      onBarcodeChange,
      onResetRow,
      onUndoRow,
    ],
  );

  return (
    <div id={`${TABLE_ID}-wrapper`} className="flex flex-col h-full mb-[.5rem]">
      <FmlxTableContainer
        id={`${TABLE_ID}-fmlxtablecontainer`}
        stickyHeader
        size="small"
        classNames={{
          base: 'edit-barcode-bulk-table',
          content: 'edit-barcode-bulk-table-content',
        }}
      >
        {tableHeaders}
        {tableBody}
      </FmlxTableContainer>
    </div>
  );
};

export default EditBarcodeBulkModalBody;
